import React, { useState, useContext, useEffect, useRef } from 'react';
import { LanguageContext, useNavigation } from './LanguageContext';
import { MoonIcon } from './icons/MoonIcon';
import { SunIcon } from './icons/SunIcon';
import { GlobeIcon } from './icons/GlobeIcon';

const Header: React.FC<{ openModal: () => void }> = ({ openModal }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t, language, setLanguage, languages } = useContext(LanguageContext);
    const { path, navigate } = useNavigation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
    const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
    const langDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    }, [theme]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
                setIsLangDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [langDropdownRef]);

    const handleLanguageChange = (langCode: string) => {
        if (language === langCode) {
            setIsLangDropdownOpen(false);
            return;
        }

        setLanguage(langCode);
        setIsLangDropdownOpen(false);
        if (isOpen) setIsOpen(false);
    };

    const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const href = e.currentTarget.getAttribute('href');
        if (!href || !href.startsWith('#')) return;

        if (isOpen) { // Close mobile menu on click
            setIsOpen(false);
        }

        const id = href.substring(1);

        const scrollToSection = () => {
            const element = document.getElementById(id);
            if (element) {
                const headerOffset = 80; // Corresponds to h-20
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        };

        if (path !== '/') {
            navigate('/');
            // Wait for the main page to render before scrolling
            setTimeout(scrollToSection, 100);
        } else {
            scrollToSection();
        }
    };

    const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        navigate('/');
    };

    const toggleTheme = () => {
        setTheme(theme === 'light' ? 'dark' : 'light');
    };

    const languageSwitcher = (
        <div className="relative" ref={langDropdownRef}>
            <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="text-gray-700 dark:text-gray-300 hover:text-accent dark:hover:text-accent p-2 rounded-full transition-colors flex items-center"
                aria-haspopup="true"
                aria-expanded={isLangDropdownOpen}
            >
                <GlobeIcon className="h-5 w-5" />
                <span className="ms-1 text-sm font-medium uppercase">{language}</span>
            </button>
            {isLangDropdownOpen && (
                <div className="absolute end-0 mt-2 w-40 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-10">
                    <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        {languages.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => handleLanguageChange(lang.code)}
                                className={`w-full text-start block px-4 py-2 text-sm ${language === lang.code ? 'font-bold text-accent' : 'text-gray-700 dark:text-gray-300'} hover:bg-gray-100 dark:hover:bg-gray-700`}
                                role="menuitem"
                            >
                                {lang.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/80 dark:bg-black/80 backdrop-blur-sm shadow-md' : 'bg-transparent'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    <div className="flex-shrink-0">
                        <a href="#/" onClick={handleLogoClick} className="inline-flex items-center gap-x-3 text-2xl text-gray-900 dark:text-white tracking-tight">
                            <span className="font-extrabold">BuildHUB</span>
                            <span className="inline-block w-1 h-6 bg-accent shrink-0" aria-hidden="true"></span>
                            <span className="font-light text-gray-500 dark:text-gray-400 shrink-0 min-w-max">GLOBAL</span>
                        </a>
                    </div>
                    <div className="hidden md:flex items-center">
                        <nav className="flex items-baseline gap-x-1 lg:gap-x-2">
                            {(t.header?.nav || []).map((item: { href: string, name: string }, index: number) => (
                                <a key={item?.name || index} href={item?.href} onClick={handleNavClick} className="text-gray-700 dark:text-gray-300 hover:text-accent dark:hover:text-accent px-2 lg:px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-300">
                                    {item?.name || ''}
                                </a>
                            ))}
                        </nav>
                        <div className="ms-4 flex items-center gap-x-2">
                            {languageSwitcher}
                            <button onClick={toggleTheme} className="text-gray-700 dark:text-gray-300 hover:text-accent dark:hover:text-accent p-2 rounded-full transition-colors">
                                <span className="sr-only">Toggle theme</span>
                                {theme === 'light' ? <MoonIcon /> : <SunIcon />}
                            </button>
                        </div>
                    </div>
                    <div className="-me-2 flex md:hidden">
                        <button onClick={toggleTheme} className="me-2 text-gray-700 dark:text-gray-300 hover:text-accent dark:hover:text-accent p-2 rounded-full transition-colors">
                            <span className="sr-only">Toggle theme</span>
                            {theme === 'light' ? <MoonIcon /> : <SunIcon />}
                        </button>
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            type="button"
                            className="bg-gray-200 dark:bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-300 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                            aria-controls="mobile-menu"
                            aria-expanded={isOpen}
                        >
                            <span className="sr-only">Open main menu</span>
                            {!isOpen ? (
                                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            ) : (
                                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {isOpen && (
                <div className="md:hidden" id="mobile-menu">
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white dark:bg-black">
                        {(t.header?.nav || []).map((item: { href: string, name: string }, index: number) => (
                            <a key={item?.name || index} href={item?.href} onClick={handleNavClick} className="text-gray-700 dark:text-gray-300 hover:text-accent dark:hover:text-accent block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">
                                {item?.name || ''}
                            </a>
                        ))}
                        <div className="border-t border-gray-200 dark:border-gray-800 mt-4 pt-4">
                            <div className="flex flex-wrap gap-2 px-3">
                                {languages.map(lang => (
                                    <button
                                        key={lang.code}
                                        onClick={() => handleLanguageChange(lang.code)}
                                        className={`px-3 py-1 rounded-full text-sm font-medium ${language === lang.code ? 'bg-accent text-accent-foreground' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}
                                    >
                                        {lang.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;